import { BaseEdge, EdgeLabelRenderer, getBezierPath, getSmoothStepPath, type Edge, type EdgeProps } from '@xyflow/react';
import { memo } from 'react';
import { cn } from '../lib/cn';
import type { GraphEdge } from './graph';
import type { MarkerId } from './Markers';
import { useDiagramSettings } from './settings';

/** `soft`: drawn softer, still readable (cascade paths are on and the delete does not travel it). */
export type EdgeData = { edge: GraphEdge; dimmed: boolean; soft?: boolean; [key: string]: unknown };
export type DiagramEdgeType = Edge<EdgeData, 'relationship'>;

/**
 * Crow's-foot notation. Next to the dependent: how many dependents one principal can have.
 * Next to the principal: whether a dependent needs one.
 */
function markers(edge: GraphEdge): { start: MarkerId | null; end: MarkerId } {
  switch (edge.kind) {
    case 'inheritance':
      return { start: null, end: 'mv-inherit' };
    case 'call':
    case 'uses':
      return { start: null, end: 'mv-arrow' };
    case 'manyToMany':
      return { start: 'mv-zero-many', end: 'mv-zero-many' };
    default: {
      const start: MarkerId = edge.unique ? (edge.requiredDependent ? 'mv-one' : 'mv-zero-one') : 'mv-zero-many';
      const end: MarkerId = edge.required ? 'mv-one' : 'mv-zero-one';
      return { start, end };
    }
  }
}

export const RelationshipEdge = memo(function RelationshipEdge(props: EdgeProps<DiagramEdgeType>) {
  const { id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, data, selected } = props;
  const smooth = useDiagramSettings((s) => s.edgeStyle === 'smooth');
  const [path, labelX, labelY] = smooth
    ? getBezierPath({ sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition, curvature: 0.35 })
    : getSmoothStepPath({ sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition, borderRadius: 10, offset: 22 });
  if (!data) return null;
  const { start, end } = markers(data.edge);
  const diff = data.edge.diff;
  const kind = data.edge.kind;
  // Dashed: optional relationships, removed ones, guessed calls, and uses (lighter than calls).
  const dashed = (kind === 'relationship' && !data.edge.required) || diff === 'removed' || !!data.edge.guess || kind === 'uses';
  const effect = data.edge.effect;
  const stroke = selected
    ? 'var(--accent)'
    : effect
      ? `var(--effect-${effect})`
      : diff
        ? `var(--${diff})`
        : kind === 'inheritance' || kind === 'uses'
          ? 'var(--text-muted)'
          : 'var(--edge)';
  const conflict = data.edge.conflict;
  return (
    <>
      <BaseEdge
        id={id}
        path={path}
        markerStart={start ? `url(#${start})` : undefined}
        markerEnd={`url(#${end})`}
        interactionWidth={14}
        style={{
          stroke,
          strokeWidth: conflict ? 3.2 : effect ? 2.4 : selected || (diff && diff !== 'removed') ? 2 : kind === 'uses' ? 1.1 : 1.4,
          strokeDasharray: dashed ? (diff === 'removed' ? '3 4' : kind === 'uses' ? '2 4' : '5 4') : undefined,
          opacity: data.dimmed ? 0.15 : data.soft || data.edge.muted ? 0.3 : diff === 'removed' ? 0.7 : kind === 'uses' ? 0.75 : 1,
        }}
      />
      {(conflict || ((selected || kind === 'manyToMany') && data.edge.label)) && (
        <EdgeLabelRenderer>
          <div
            className={cn('mv-edge-label nodrag nopan', conflict && 'mv-edge-conflict')}
            style={{ transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`, opacity: data.dimmed ? 0.2 : data.soft ? 0.5 : 1 }}
          >
            {conflict ?? data.edge.label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
});
